import { useQueryClient } from "@tanstack/react-query";
import { doc, getDoc, getFirestore, runTransaction, setDoc, updateDoc } from "firebase/firestore";
import { type ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { AiOutlineUser } from "react-icons/ai";
import { BsTelephone } from "react-icons/bs";
import { FaRegAddressBook, FaRoute } from "react-icons/fa";
import { HiOutlineIdentification } from "react-icons/hi2";
import { IoLocationOutline, IoStorefrontOutline } from "react-icons/io5";
import { toast } from "react-toastify";
import { useClientesActions, useRutasActions } from "../../../context/AppContext";
import {
  construirAllClientsPayload,
  resolveAllClientsDocRef,
  uploadImage,
  uploadImageTienda,
} from "../../../pages/HomePage/Components/firebaseFunctions";
import { queryKeys } from "../../../queries/queryKeys";
import {
  calcularFechaFinalCredito,
  calcularPagoDiarioCredito,
  formatearFechaActualDisplay,
  leerArchivoComoPreview,
  OPCIONES_FORMA_PAGO_CREDITO,
} from "../../../utils/clienteCredito";
import {
  aplicarMovimientoARutaLocal,
  construirActualizacionRutaConMovimiento,
  crearMovimientoRutaData,
  crearMovimientoRutaRef,
} from "../../../utils/rutaMovimientos";
import type {
  AnyObject,
  CampoEntradaConfig,
  Cliente,
  EditarClienteFormData,
  EditarClienteModalProps,
  UseEditarClienteModalReturn,
} from "./types";

const crearDatosClienteDesdeCliente = (
  cliente: Cliente | null | undefined,
): EditarClienteFormData => ({
  abono: cliente?.abono ?? 0,
  actualizado: cliente?.actualizado ?? false,
  cpf: cliente?.cpf ?? "",
  cuotasAtrasadas: cliente?.cuotasAtrasadas ?? 0,
  cuotasPactadas: cliente?.cuotasPactadas ?? 0,
  cuotasPagadas: cliente?.cuotasPagadas ?? 0,
  descripcion: cliente?.descripcion ?? "",
  direccion: cliente?.direccion ?? "",
  direccionCobro: cliente?.direccionCobro ?? "",
  fechaFinal: cliente?.fechaFinal ?? "",
  fechaUltimoAbono: cliente?.fechaUltimoAbono ?? "",
  formaDePago: cliente?.formaDePago ?? "diario",
  historialPagos: cliente?.historialPagos ?? [],
  imageTienda: cliente?.imageTienda ?? "",
  imageUrl: cliente?.imageUrl ?? "",
  nombreCliente: cliente?.nombreCliente ?? "",
  pagoDiario: cliente?.pagoDiario ?? 0,
  porcentajeInteres: cliente?.porcentajeInteres ?? 0,
  telefono: cliente?.telefono ?? "",
  totalAbono: cliente?.totalAbono ?? 0,
  ubicacion: cliente?.ubicacion ?? "",
  valorPico: cliente?.valorPico ?? 0,
  valorPrestamo: cliente?.valorPrestamo ?? 0,
});

const crearFileChangeHandler = (
  field: "imageTienda" | "imageUrl",
  setPreview: (result: string | null) => void,
  actualizarDatosCliente: (
    updater: (current: EditarClienteFormData) => EditarClienteFormData,
  ) => void,
) => {
  return (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    actualizarDatosCliente((current) => ({
      ...current,
      [field]: file,
    }));
    leerArchivoComoPreview(file, (result) => {
      setPreview(typeof result === "string" ? result : null);
    });
  };
};

const recalcularDatosCredito = (
  datosCliente: EditarClienteFormData,
  fechaBase: string,
  overrides?: Partial<
    Pick<
      EditarClienteFormData,
      "cuotasPactadas" | "formaDePago" | "porcentajeInteres" | "valorPrestamo"
    >
  >,
): EditarClienteFormData => {
  const nextDatosCliente = {
    ...datosCliente,
    ...overrides,
  };

  return {
    ...nextDatosCliente,
    fechaFinal: calcularFechaFinalCredito({
      avanzarUnDiaInicial: false,
      cuotasPactadas: nextDatosCliente.cuotasPactadas,
      fechaBase,
      formaDePago: nextDatosCliente.formaDePago,
    }),
    pagoDiario: calcularPagoDiarioCredito(
      nextDatosCliente.valorPrestamo,
      nextDatosCliente.porcentajeInteres,
      nextDatosCliente.cuotasPactadas,
    ),
  };
};

const calcularAjustePrestamo = (
  saldoActual: number,
  valorPrestamoAnterior: number,
  valorPrestamoActual: number,
  nombreCliente: string,
): {
  descripcion: string;
  montoFinal: number;
  saldoActualizado: number;
} => {
  if (valorPrestamoAnterior > valorPrestamoActual) {
    const diferencia = valorPrestamoAnterior - valorPrestamoActual;

    return {
      descripcion: "Prestamo Editado (resta)",
      montoFinal: diferencia,
      saldoActualizado: saldoActual + diferencia,
    };
  }

  if (valorPrestamoActual > valorPrestamoAnterior) {
    const diferencia = valorPrestamoActual - valorPrestamoAnterior;

    return {
      descripcion: "Prestamo Editado (suma)",
      montoFinal: diferencia,
      saldoActualizado: saldoActual - diferencia,
    };
  }

  return {
    descripcion: `Cliente editado ${nombreCliente}`,
    montoFinal: 0,
    saldoActualizado: saldoActual,
  };
};

export const useEditarClienteModal = ({
  selectedCliente,
  setClientes,
  setIsModalEdit,
  setSelectedCliente,
  usuarioRuta,
}: EditarClienteModalProps): UseEditarClienteModalReturn | null => {
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageTiendaPreview, setImageTiendaPreview] = useState<string | null>(null);
  const [datosCliente, setDatosCliente] = useState<EditarClienteFormData>(() =>
    crearDatosClienteDesdeCliente(selectedCliente),
  );
  const [isSubmiting, setIsSubmiting] = useState(false);
  const { setRutasData } = useRutasActions();
  const { setInfoClientes } = useClientesActions();
  const queryClient = useQueryClient();
  const fechaActual = selectedCliente?.fechaActual ?? formatearFechaActualDisplay();

  useEffect(() => {
    if (!selectedCliente) {
      return;
    }

    setDatosCliente(crearDatosClienteDesdeCliente(selectedCliente));
    setImagePreview(null);
    setImageTiendaPreview(null);
  }, [selectedCliente]);

  const handleClose = useCallback(() => {
    setIsModalEdit(false);
  }, [setIsModalEdit]);

  const actualizarDatosCliente = useCallback(
    (updater: (current: EditarClienteFormData) => EditarClienteFormData) => {
      setDatosCliente(updater);
    },
    [],
  );

  const handleImageChange = useMemo(
    () => crearFileChangeHandler("imageUrl", setImagePreview, actualizarDatosCliente),
    [actualizarDatosCliente],
  );

  const handleImageTiendaChange = useMemo(
    () => crearFileChangeHandler("imageTienda", setImageTiendaPreview, actualizarDatosCliente),
    [actualizarDatosCliente],
  );

  const obtenerUbicacionActual = useCallback(() => {
    if (!navigator.geolocation) {
      console.error("La geolocalizacion no esta soportada por este navegador.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;

        actualizarDatosCliente((current) => ({
          ...current,
          ubicacion: `${latitude}, ${longitude}`,
        }));
      },
      (error) => {
        console.error("Error obteniendo la ubicacion:", error);
      },
    );
  }, [actualizarDatosCliente]);

  const handleValorPrestamoChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const valorPrestamo = Number(event.target.value) || 0;

      actualizarDatosCliente((current) =>
        recalcularDatosCredito(current, fechaActual, {
          valorPrestamo,
        }),
      );
    },
    [actualizarDatosCliente, fechaActual],
  );

  const handlePorcentajeInteresChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const porcentajeInteres = Number(event.target.value) || 0;

      actualizarDatosCliente((current) =>
        recalcularDatosCredito(current, fechaActual, {
          porcentajeInteres,
        }),
      );
    },
    [actualizarDatosCliente, fechaActual],
  );

  const handleCuotasPactadasChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const cuotasPactadas = Number(event.target.value) || 0;

      actualizarDatosCliente((current) =>
        recalcularDatosCredito(current, fechaActual, {
          cuotasPactadas,
        }),
      );
    },
    [actualizarDatosCliente, fechaActual],
  );

  const handleFormaDePagoChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const formaDePago = event.target.value;

      actualizarDatosCliente((current) =>
        recalcularDatosCredito(current, fechaActual, {
          formaDePago,
        }),
      );
    },
    [actualizarDatosCliente, fechaActual],
  );

  const handleCampoTextoChange = useCallback(
    (
      field:
        | "cpf"
        | "nombreCliente"
        | "direccionCobro"
        | "telefono"
        | "direccion"
        | "descripcion"
        | "ubicacion",
    ) =>
      (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        actualizarDatosCliente((current) => ({
          ...current,
          [field]: value,
        }));
      },
    [actualizarDatosCliente],
  );

  const editarCliente = useCallback<UseEditarClienteModalReturn["editarCliente"]>(
    async (event) => {
      if (!selectedCliente) {
        return;
      }

      event.preventDefault();

      try {
        setIsSubmiting(true);

        const db = getFirestore();
        const clienteRef = doc(
          db,
          "admin_users",
          usuarioRuta.adminUid,
          "rutas",
          usuarioRuta.uid,
          "clientes",
          selectedCliente.uid ?? "",
        );

        const imageUrlPromise = imagePreview
          ? uploadImage({
              image: datosCliente.imageUrl,
              nombre: datosCliente.nombreCliente,
            })
          : Promise.resolve(datosCliente.imageUrl);
        const imageTiendaUrlPromise = imageTiendaPreview
          ? uploadImageTienda({
              descripcion: selectedCliente.uid,
              imageTienda: datosCliente.imageTienda,
            })
          : Promise.resolve(datosCliente.imageTienda);

        const [imageUrl, imageTiendaUrl] = await Promise.all([
          imageUrlPromise,
          imageTiendaUrlPromise,
        ]);

        const clienteSnapshot = await getDoc(clienteRef);
        const clienteData = clienteSnapshot.data() as AnyObject;

        if (!clienteData) {
          throw new Error("No se encontro la informacion del cliente");
        }

        const updatedDatosCliente = {
          ...datosCliente,
          imageTienda: imageTiendaUrl ? imageTiendaUrl : selectedCliente.imageTienda,
          imageUrl: imageUrl ? imageUrl : selectedCliente.imageUrl,
        };
        const allClienteRef = await resolveAllClientsDocRef({
          cliente: selectedCliente,
          db,
          usuarioRuta,
        });

        const rutaRef = doc(db, "admin_users", usuarioRuta.adminUid, "rutas", usuarioRuta.uid);
        const fechaMovimiento = new Date();
        const movimientoRef = crearMovimientoRutaRef(db, usuarioRuta.adminUid, usuarioRuta.uid);
        const resultadoTransaccion = await runTransaction(db, async (transaction) => {
          const rutaSnapshot = await transaction.get(rutaRef);
          const rutaData = rutaSnapshot.data() as AnyObject;

          if (!rutaData) {
            throw new Error("No se encontro la informacion de la ruta");
          }

          const valorPrestamoAnterior = Number(clienteData.valorPrestamo ?? 0);
          const valorPrestamoActual = Number(datosCliente.valorPrestamo ?? 0);
          const ajustePrestamo = calcularAjustePrestamo(
            Number(rutaData.saldoInicial ?? 0),
            valorPrestamoAnterior,
            valorPrestamoActual,
            datosCliente.nombreCliente,
          );
          const movimiento = crearMovimientoRutaData({
            clienteUid: selectedCliente.uid,
            descripcion: ajustePrestamo.descripcion,
            fecha: fechaMovimiento,
            monto: ajustePrestamo.montoFinal,
            origen: "editar_cliente",
            responsable: "Admin",
            saldoDespues: ajustePrestamo.saldoActualizado,
            tipo: ajustePrestamo.montoFinal === 0 ? "ingreso" : undefined,
          });
          const rutaUpdates = construirActualizacionRutaConMovimiento(
            rutaData,
            movimiento,
            ajustePrestamo.saldoActualizado,
          );

          transaction.set(movimientoRef, movimiento);
          transaction.update(rutaRef, rutaUpdates);

          return {
            ajustePrestamo,
            movimiento,
          };
        });

        setSelectedCliente((prevSelectedCliente) =>
          prevSelectedCliente?.uid === selectedCliente.uid
            ? { ...prevSelectedCliente, ...updatedDatosCliente }
            : prevSelectedCliente,
        );

        setClientes((prevClientes) =>
          prevClientes.map((cliente) =>
            cliente.uid === selectedCliente.uid ? { ...cliente, ...updatedDatosCliente } : cliente,
          ),
        );

        // setClientes de arriba solo actualiza el estado local de la tabla;
        // patcheamos tambien la cache compartida de "clientes".
        setInfoClientes(
          (prevClientes) =>
            prevClientes?.map((cliente) =>
              cliente.uid === selectedCliente.uid
                ? { ...cliente, ...updatedDatosCliente }
                : cliente,
            ) ?? prevClientes,
        );

        setRutasData((prevRutas) =>
          prevRutas
            ? prevRutas.map((ruta) =>
                ruta.uid === usuarioRuta.uid
                  ? aplicarMovimientoARutaLocal(
                      ruta,
                      resultadoTransaccion.movimiento,
                      resultadoTransaccion.ajustePrestamo.saldoActualizado,
                    )
                  : ruta,
              )
            : prevRutas,
        );
        void queryClient.invalidateQueries({
          queryKey: queryKeys.movimientos(usuarioRuta.adminUid, usuarioRuta.uid),
        });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.movimientosDelDiaByRuta(usuarioRuta.adminUid, usuarioRuta.uid),
        });

        await updateDoc(clienteRef, updatedDatosCliente);

        if (allClienteRef) {
          await setDoc(
            allClienteRef,
            construirAllClientsPayload(
              updatedDatosCliente,
              selectedCliente.uid,
              updatedDatosCliente.imageUrl,
              updatedDatosCliente.imageTienda,
              selectedCliente.perfilUid ? undefined : (selectedCliente.createdAt ?? new Date()),
              { ultimoCreditoUid: selectedCliente.uid },
            ),
            { merge: true },
          );
          void queryClient.invalidateQueries({
            queryKey: queryKeys.allClients(usuarioRuta.adminUid, usuarioRuta.uid),
          });
        }

        toast.success("Cliente editado con exito");
        setIsModalEdit(false);
      } catch (error) {
        console.error("Error al editar el cliente:", error);
      } finally {
        setIsSubmiting(false);
      }
    },
    [
      usuarioRuta,
      datosCliente,
      imagePreview,
      imageTiendaPreview,
      queryClient,
      selectedCliente,
      setClientes,
      setInfoClientes,
      setIsModalEdit,
      setRutasData,
      setSelectedCliente,
    ],
  );

  const campos = useMemo<CampoEntradaConfig[]>(
    () => [
      {
        Icon: HiOutlineIdentification,
        onChange: handleCampoTextoChange("cpf"),
        placeholder: "CPF",
        valor: datosCliente.cpf,
      },
      {
        Icon: AiOutlineUser,
        onChange: handleCampoTextoChange("nombreCliente"),
        placeholder: "Nombre del Cliente",
        valor: datosCliente.nombreCliente,
      },
      {
        Icon: FaRoute,
        onChange: handleCampoTextoChange("direccionCobro"),
        placeholder: "Direccion de Cobro",
        valor: datosCliente.direccionCobro,
      },
      {
        Icon: BsTelephone,
        onChange: handleCampoTextoChange("telefono"),
        placeholder: "Telefono",
        valor: datosCliente.telefono,
      },
      {
        Icon: FaRegAddressBook,
        onChange: handleCampoTextoChange("direccion"),
        placeholder: "Direccion de Residencia",
        valor: datosCliente.direccion,
      },
      {
        Icon: IoStorefrontOutline,
        onChange: handleCampoTextoChange("descripcion"),
        placeholder: "Descripcion de la tienda",
        valor: datosCliente.descripcion,
      },
      {
        Icon: IoLocationOutline,
        onChange: handleCampoTextoChange("ubicacion"),
        onClick: obtenerUbicacionActual,
        placeholder: "Ubicacion Actual",
        readOnly: true,
        valor: datosCliente.ubicacion,
      },
    ],
    [
      datosCliente.cpf,
      datosCliente.descripcion,
      datosCliente.direccion,
      datosCliente.direccionCobro,
      datosCliente.nombreCliente,
      datosCliente.telefono,
      datosCliente.ubicacion,
      handleCampoTextoChange,
      obtenerUbicacionActual,
    ],
  );

  if (!selectedCliente) {
    return null;
  }

  return {
    campos,
    datosCliente,
    editarCliente,
    fechaActual,
    handleClose,
    handleCuotasPactadasChange,
    handleFormaDePagoChange,
    handleImageChange,
    handleImageTiendaChange,
    handlePorcentajeInteresChange,
    handleValorPrestamoChange,
    imagePreview,
    imageTiendaPreview,
    isSubmiting,
    opcionesFormaPago: [...OPCIONES_FORMA_PAGO_CREDITO],
    selectedCliente,
  };
};
